import React, { useEffect, useState } from "react";
import { BrushCleaning as Broom, Loader2, RefreshCw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function CleanupCard({ onDone }) {
  const [preview, setPreview] = useState(null);
  const [abandoned, setAbandoned] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/cleanup/test-data").then(({ data }) => setPreview(data)).catch(() => setPreview({ groups: [], total: 0, abandoned_orders: 0 }));
  useEffect(() => { load(); }, []);

  const total = (preview?.total || 0) + (abandoned ? preview?.abandoned_orders || 0 : 0);

  const run = async () => {
    if (!window.confirm(`Permanently remove ${total} test record${total === 1 ? "" : "s"}? Real customers, paid orders and your admin account are never touched.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/cleanup/test-data", { include_abandoned: abandoned, abandoned_hours: 48 });
      toast.success(data.total ? `Swept away ${data.total} test record${data.total === 1 ? "" : "s"} 🧹` : "Nothing to clean — already spotless");
      await load();
      onDone?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!preview) return null;
  const clean = preview.total === 0 && preview.abandoned_orders === 0;
  return (
    <div className="bg-white rounded-3xl soft-shadow p-6 mb-4" data-testid="cleanup-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display font-bold text-[#19324A] mb-1 inline-flex items-center gap-2"><Broom className="w-4 h-4 text-[#FF6B9D]" /> Remove test data</h3>
          <p className="text-xs text-[#19324A]/50">Clears orders, sign-ups, accounts and messages from test addresses (example.com, @x.com, test_… etc.) so every number here is a real customer.</p>
        </div>
        <button type="button" onClick={load} className="p-2 rounded-full hover:bg-[#19324A]/5" title="Re-scan" data-testid="cleanup-rescan"><RefreshCw className="w-4 h-4 text-[#19324A]/60" /></button>
      </div>
      {clean ? (
        <p className="mt-4 text-sm font-semibold text-[#166534]" data-testid="cleanup-clean">All clear — no test records found 🐾</p>
      ) : (
        <>
          <ul className="mt-4 grid sm:grid-cols-2 gap-2" data-testid="cleanup-groups">
            {preview.groups.map((g) => (
              <li key={g.collection} className="rounded-2xl bg-[#FFF9EE] px-4 py-2.5" data-testid={`cleanup-group-${g.collection}`}>
                <p className="text-sm font-bold text-[#19324A]">{g.label} <span className="text-[#FF6B9D]">· {g.count}</span></p>
                <p className="text-[11px] text-[#19324A]/50 truncate">{g.sample.join(", ")}</p>
              </li>
            ))}
          </ul>
          {preview.abandoned_orders > 0 && (
            <label className="mt-4 flex items-start gap-2 text-sm text-[#19324A] cursor-pointer">
              <input type="checkbox" checked={abandoned} onChange={(e) => setAbandoned(e.target.checked)} className="mt-1 h-4 w-4 accent-[#FF6B9D]" data-testid="cleanup-abandoned" />
              <span>Also remove <strong>{preview.abandoned_orders}</strong> abandoned checkout{preview.abandoned_orders === 1 ? "" : "s"} (unpaid for 48h+) from real addresses — they never became orders.</span>
            </label>
          )}
          <button type="button" onClick={run} disabled={busy || total === 0} className="btn-pink !py-2.5 mt-4 disabled:opacity-50 inline-flex items-center gap-2" data-testid="cleanup-run">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Broom className="w-4 h-4" />} Remove {total} test record{total === 1 ? "" : "s"}
          </button>
        </>
      )}
    </div>
  );
}
